import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { attendanceApi } from '@/services/attendance.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Spinner } from '@/components/ui';
import { Avatar } from '@/components/avatar';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Scans a printed credential's QR code to check someone in at the door.
 *
 * `PersonQrCode` encodes nothing but the person's id — decoded here with
 * `jsqr` from a live camera frame, then confirmed with a real
 * `GET /people/:id` before anything is recorded, so a staff member sees
 * who they are about to check in rather than trusting an unverified scan.
 * No backend changes: this composes `GET /people/:id` and the existing
 * `POST /attendance` exactly as the manual check-in dialog does.
 */
export function QrCheckInPage() {
  const { t } = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [scannedPersonId, setScannedPersonId] = useState<string | null>(null);
  const [checkedInName, setCheckedInName] = useState<string | null>(null);

  const scannedPerson = useQuery({
    queryKey: ['people', scannedPersonId],
    queryFn: ({ signal }) => peopleApi.get(scannedPersonId!, signal),
    enabled: scannedPersonId !== null,
  });

  const checkIn = useMutation({
    mutationFn: (personId: string) => attendanceApi.checkIn({ personId, contextType: 'GENERAL' }),
    onSuccess: () => {
      setCheckedInName(scannedPerson.data?.displayName ?? null);
      setScannedPersonId(null);
    },
  });

  // Camera lifecycle: request the back camera, feed it to a hidden
  // <video>, and stop the stream on unmount rather than leaving it
  // running once the operator navigates away.
  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;

    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'environment' } })
      .then((mediaStream) => {
        if (cancelled) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = mediaStream;
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          void videoRef.current.play();
        }
      })
      .catch(() => setCameraError(t('attendance.scan.cameraError')));

    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [t]);

  // Decoding loop: only runs while nothing is already scanned/pending, so
  // a code already being confirmed or checked in is not re-read mid-flow.
  useEffect(() => {
    if (scannedPersonId || checkIn.isPending) return;

    let frameId: number;
    function tick(): void {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (context) {
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          if (code?.data && UUID_PATTERN.test(code.data.trim())) {
            setScannedPersonId(code.data.trim());
            return;
          }
        }
      }
      frameId = requestAnimationFrame(tick);
    }
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [scannedPersonId, checkIn.isPending]);

  return (
    <div className="page">
      <h1 className="page-title">{t('attendance.scan.title')}</h1>

      {cameraError && <Alert tone="danger">{cameraError}</Alert>}
      {checkIn.isError && (
        <Alert tone="danger">
          {checkIn.error instanceof ApiError
            ? t(`errors.${checkIn.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}
      {checkedInName && (
        <Alert tone="success">
          {t('attendance.scan.checkedIn', { values: { name: checkedInName } })}
        </Alert>
      )}

      <video
        ref={videoRef}
        className="qr-scan-video"
        playsInline
        muted
        hidden={scannedPersonId !== null}
      />
      <canvas ref={canvasRef} hidden />

      {scannedPersonId && scannedPerson.isPending && <Spinner label={t('common.states.loading')} />}

      {scannedPersonId && scannedPerson.data && (
        <div className="qr-scan-confirm">
          <Avatar
            photoFileId={scannedPerson.data.photoFileId}
            displayName={scannedPerson.data.displayName}
            size={64}
          />
          <p className="print-credential-name">{scannedPerson.data.displayName}</p>
          <div className="dialog-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setScannedPersonId(null)}
              disabled={checkIn.isPending}
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={checkIn.isPending}
              onClick={() => checkIn.mutate(scannedPersonId)}
            >
              {t('attendance.record.checkIn')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
